import React, { useState } from 'react';
import { GradientModal, MonthDayPicker, SeasonPicker } from '../ui';
import { TYPES, getCategoryOptions, getFormConfig, createEmptyBatchEntry } from './constants';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabaseClient';
import { getPlantIcon, SPACING_MAX_CHARS } from '../../utils';

/**
 * QuickAddSeedModal - A modal for adding a single custom seed directly from a
 * workflow like the GardenLayoutPlanner cell assignment flow.
 *
 * On save: inserts the seed to the database, fires `seed-added-externally`,
 * and calls `onSave(newSeedRecord)` so the caller can chain actions
 * (e.g., assign the new plant to a bed cell).
 *
 * @param {boolean}  props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.onSave  - Called with the inserted seed row on success
 * @param {boolean}  props.isPro
 * @param {number}   props.seedLimit
 * @param {number}   props.currentSeedCount
 */
const QuickAddSeedModal = ({ isOpen, onClose, onSave, isPro, seedLimit, currentSeedCount }) => {
    const { user } = useAuth();
    const [entry, setEntry] = useState(() => createEmptyBatchEntry());
    const [saving, setSaving] = useState(false);

    const update = (field, value) => setEntry(prev => ({ ...prev, [field]: value }));

    const formConfig = getFormConfig(entry.type, entry.category);
    const categoryOptions = getCategoryOptions(entry.type);

    const handleClose = () => {
        setEntry(createEmptyBatchEntry());
        onClose();
    };

    const handleTypeChange = (newType) => {
        const validCategories = getCategoryOptions(newType).map(c => c.id);
        setEntry(prev => ({
            ...prev,
            type: newType,
            category: validCategories.includes(prev.category) ? prev.category : '',
            // Clear type-specific fields on switch
            ...(newType === 'seed'
                ? { years_to_production: '' }
                : { season: '', days_to_production: '', weeks_indoors_before_transplant: '', preferred_start_method: '' })
        }));
    };

    const handleSave = async () => {
        if (!entry.plant.trim()) {
            alert('Please enter a plant name.');
            return;
        }
        if (!entry.type || !entry.category) {
            alert('Type and Category are required.');
            return;
        }
        if (entry.type === 'seed' && !entry.season) {
            alert('Season is required for seeds. Please select Cool or Warm.');
            return;
        }
        if (!isPro && currentSeedCount >= seedLimit) {
            alert(`You've reached the free limit of ${seedLimit} plants. Upgrade to Pro to add more.`);
            return;
        }

        setSaving(true);
        try {
            const isAnnual = entry.type === 'seed';
            const seedData = {
                user_id: user.id,
                type: entry.type,
                category: entry.category,
                plant: entry.plant.trim(),
                variety: entry.variety.trim() || null,
                quantity: entry.quantity || null,
                notes: entry.notes.trim() || null,
                need_to_buy: entry.need_to_buy,
                season: isAnnual ? (entry.season || null) : null,
                days_to_production:
                    isAnnual && entry.days_to_production && Number.isFinite(parseInt(entry.days_to_production, 10))
                        ? parseInt(entry.days_to_production, 10) : null,
                weeks_indoors_before_transplant:
                    isAnnual && entry.weeks_indoors_before_transplant && Number.isFinite(parseInt(entry.weeks_indoors_before_transplant, 10))
                        ? parseInt(entry.weeks_indoors_before_transplant, 10) : null,
                preferred_start_method: isAnnual ? (entry.preferred_start_method || null) : null,
                harvest_season: entry.harvest_season || null,
                years_to_production: !isAnnual ? (entry.years_to_production || null) : null,
                planned_start_date: isAnnual && entry.planned_start_date ? entry.planned_start_date : null,
                planned_transplant_date: isAnnual && entry.planned_transplant_date ? entry.planned_transplant_date : null,
                frost_tolerant: entry.frost_tolerant || false,
                spacing: entry.spacing.trim() || null,
                sun_requirement: isAnnual ? (entry.sun_requirement || null) : null,
                water_needs: isAnnual ? (entry.water_needs || null) : null,
                direct_sow_weeks_before_last_frost:
                    isAnnual && entry.direct_sow_weeks_before_last_frost && Number.isFinite(parseInt(entry.direct_sow_weeks_before_last_frost, 10))
                        ? parseInt(entry.direct_sow_weeks_before_last_frost, 10) : null,
                icon: getPlantIcon(entry.plant, null, entry.variety),
            };

            const { data, error } = await supabase.from('seeds').insert(seedData).select().single();

            if (error || !data) {
                alert("Couldn't save — please try again.");
                return;
            }

            // Broadcast so SeedInventory (if mounted) refreshes
            window.dispatchEvent(new CustomEvent('seed-added-externally', { detail: { seed: data } }));

            setEntry(createEmptyBatchEntry());
            onSave(data);
        } finally {
            setSaving(false);
        }
    };

    return (
        <GradientModal
            isOpen={isOpen}
            onClose={handleClose}
            title="Add Custom Seed"
            subtitle="Add a plant to your inventory — it will be assigned to the selected cells"
            maxWidth="max-w-2xl"
            zIndex="z-[80]"
            primaryAction={{ label: 'Save', onClick: handleSave, loading: saving }}
            secondaryAction={{ label: 'Cancel', onClick: handleClose }}
        >
            <div className="space-y-6">

                {/* Row 1: Type, Category, Plant, Variety */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <label className="block text-sm font-semibold text-[#2F5233] mb-1">
                            Type <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full ml-0.5 align-middle"></span>
                        </label>
                        <select
                            value={entry.type}
                            onChange={(e) => handleTypeChange(e.target.value)}
                            className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base bg-white"
                        >
                            {TYPES.map(t => (
                                <option key={t.id} value={t.id}>{t.emoji} {t.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-[#2F5233] mb-1">
                            Category <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full ml-0.5 align-middle"></span>
                        </label>
                        <select
                            value={entry.category}
                            onChange={(e) => update('category', e.target.value)}
                            className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base bg-white"
                        >
                            <option value="">Select...</option>
                            {categoryOptions.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.emoji} {cat.label}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-[#2F5233] mb-1">
                            Plant <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full ml-0.5 align-middle"></span>
                        </label>
                        <input
                            type="text"
                            value={entry.plant}
                            onChange={(e) => update('plant', e.target.value)}
                            placeholder="Plant name"
                            className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base"
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-[#2F5233] mb-1">Variety</label>
                        <input
                            type="text"
                            value={entry.variety}
                            onChange={(e) => update('variety', e.target.value)}
                            placeholder="Optional"
                            className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base"
                        />
                    </div>
                </div>

                {/* Planting Information — Seeds/Annuals */}
                {formConfig.showSeason && (
                    <div>
                        <div className="border-b border-[#e8f5e9] pb-1.5 mb-3">
                            <span className="text-[13px] font-semibold text-[#52A675] uppercase tracking-wide">Planting Information</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                                <label className="block text-sm font-semibold text-[#2F5233] mb-1">
                                    Season <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full ml-0.5 align-middle"></span>
                                </label>
                                <select
                                    value={entry.season}
                                    onChange={(e) => update('season', e.target.value)}
                                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base bg-white"
                                >
                                    <option value="">Select...</option>
                                    <option value="warm">☀️ Warm</option>
                                    <option value="cool">❄️ Cool</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-[#2F5233] mb-1">{formConfig.productionLabel}</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={entry.days_to_production}
                                    onChange={(e) => update('days_to_production', e.target.value)}
                                    placeholder="e.g., 75"
                                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-[#2F5233] mb-1">Typical start method</label>
                                <select
                                    value={entry.preferred_start_method}
                                    onChange={(e) => update('preferred_start_method', e.target.value)}
                                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base bg-white"
                                >
                                    <option value="">Select... (optional)</option>
                                    <option value="indoor">🌱 Start Indoors</option>
                                    <option value="direct_sow">🌿 Direct Sow</option>
                                </select>
                            </div>

                            <div className={entry.preferred_start_method === 'direct_sow' ? 'opacity-50' : ''}>
                                <label className="block text-sm font-semibold text-[#2F5233] mb-1">Start indoors (weeks)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={entry.weeks_indoors_before_transplant}
                                    onChange={(e) => update('weeks_indoors_before_transplant', e.target.value)}
                                    placeholder="e.g., 6"
                                    disabled={entry.preferred_start_method === 'direct_sow'}
                                    className={`w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base ${entry.preferred_start_method === 'direct_sow' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                />
                            </div>

                            <div className={entry.preferred_start_method === 'indoor' ? 'opacity-50' : ''}>
                                <label className="block text-sm font-semibold text-[#2F5233] mb-1">Sow outdoors (wks before last frost)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={entry.direct_sow_weeks_before_last_frost}
                                    onChange={(e) => update('direct_sow_weeks_before_last_frost', e.target.value)}
                                    placeholder="e.g., 4"
                                    disabled={entry.preferred_start_method === 'indoor'}
                                    className={`w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base ${entry.preferred_start_method === 'indoor' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-[#2F5233] mb-1">Spacing</label>
                                <input
                                    type="text"
                                    value={entry.spacing}
                                    onChange={(e) => update('spacing', e.target.value.slice(0, SPACING_MAX_CHARS))}
                                    placeholder="e.g. 12 inches"
                                    maxLength={SPACING_MAX_CHARS}
                                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-[#2F5233] mb-1">Sun</label>
                                <select
                                    value={entry.sun_requirement}
                                    onChange={(e) => update('sun_requirement', e.target.value)}
                                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base bg-white"
                                >
                                    <option value="">Select... (optional)</option>
                                    <option value="full_sun">☀️ Full Sun</option>
                                    <option value="part_sun">⛅ Part Sun</option>
                                    <option value="shade_tolerant">🌥️ Shade Tolerant</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-[#2F5233] mb-1">Water Needs</label>
                                <select
                                    value={entry.water_needs}
                                    onChange={(e) => update('water_needs', e.target.value)}
                                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base bg-white"
                                >
                                    <option value="">Select... (optional)</option>
                                    <option value="low">💧 Low</option>
                                    <option value="moderate">💧💧 Moderate</option>
                                    <option value="high">💧💧💧 High</option>
                                </select>
                            </div>

                            <div>
                                <SeasonPicker
                                    value={Array.isArray(entry.harvest_season) ? entry.harvest_season : []}
                                    onChange={(seasons) => update('harvest_season', seasons)}
                                    isFlower={entry.category === 'flower'}
                                    size="sm"
                                />
                            </div>

                            <div className="flex items-end pb-1">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={entry.frost_tolerant || false}
                                        onChange={(e) => update('frost_tolerant', e.target.checked)}
                                        className="w-4 h-4 rounded border-gray-300 text-[#52A675] focus:ring-[#52A675]"
                                    />
                                    <span className="text-sm font-semibold text-[#2F5233]">Frost Tolerant</span>
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                {/* Planting Information — Perennials/Trees/Bushes */}
                {!formConfig.showSeason && (
                    <div>
                        <div className="border-b border-[#e8f5e9] pb-1.5 mb-3">
                            <span className="text-[13px] font-semibold text-[#52A675] uppercase tracking-wide">Planting Information</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                                <SeasonPicker
                                    value={Array.isArray(entry.harvest_season) ? entry.harvest_season : []}
                                    onChange={(seasons) => update('harvest_season', seasons)}
                                    isFlower={entry.category === 'flower'}
                                    size="sm"
                                />
                            </div>
                            {formConfig.showYearsToProduction && (
                                <div>
                                    <label className="block text-sm font-semibold text-[#2F5233] mb-1">{formConfig.yearsLabel}</label>
                                    <input
                                        type="text"
                                        value={entry.years_to_production}
                                        onChange={(e) => update('years_to_production', e.target.value)}
                                        placeholder="e.g., 2 or 3-5"
                                    className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Planning — Only for Seeds/Annuals */}
                {formConfig.showSeason && (
                    <div>
                        <div className="border-b border-[#e8f5e9] pb-1.5 mb-3">
                            <span className="text-[13px] font-semibold text-[#52A675] uppercase tracking-wide">Planning</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-sm font-semibold text-[#2F5233] mb-1">Planned Start Date</label>
                                <MonthDayPicker
                                    value={entry.planned_start_date}
                                    onChange={(val) => update('planned_start_date', val)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-[#2F5233] mb-1">Planned Transplant Date</label>
                                <MonthDayPicker
                                    value={entry.planned_transplant_date}
                                    onChange={(val) => update('planned_transplant_date', val)}
                                    disabled={entry.preferred_start_method === 'direct_sow'}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Inventory Tracking */}
                <div>
                    <div className="border-b border-[#e8f5e9] pb-1.5 mb-3">
                        <span className="text-[13px] font-semibold text-[#52A675] uppercase tracking-wide">Inventory Tracking</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
                        <div>
                            <label className="block text-sm font-semibold text-[#2F5233] mb-1">Quantity</label>
                            <input
                                type="text"
                                value={entry.quantity}
                                onChange={(e) => update('quantity', e.target.value)}
                                placeholder="e.g., 25 seeds"
                                className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base"
                            />
                        </div>
                        <div className="flex items-end pb-1">
                            <label className="flex items-center gap-2 cursor-pointer mt-6">
                                <input
                                    type="checkbox"
                                    checked={entry.need_to_buy}
                                    onChange={(e) => update('need_to_buy', e.target.checked)}
                                    className="w-4 h-4 text-[#52A675] border-gray-300 rounded focus:ring-[#52A675]"
                                />
                                <span className="text-sm font-semibold text-[#2F5233]">Need to buy</span>
                            </label>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-[#2F5233] mb-1">Notes</label>
                        <textarea
                            value={entry.notes}
                            onChange={(e) => update('notes', e.target.value)}
                            placeholder="Optional notes"
                            rows={2}
                            className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] text-base resize-y"
                        />
                    </div>
                </div>

            </div>
        </GradientModal>
    );
};

export default QuickAddSeedModal;
